import type { ReactiveController, ReactiveControllerHost } from "lit";
import { createFocusTrap, FocusTrap } from "focus-trap";

/**
 * Dialog Accessibility Controller
 *
 * Owns the accessibility behaviour every modal dialog needs: focus trap,
 * Escape-to-dismiss, body scroll lock, focus restoration, and hiding the
 * background from assistive technology.
 *
 * Exists as a controller rather than a base class because a single component
 * can host more than one dialog — `customization-modal` renders a trigger
 * button plus two dialogs, so it instantiates this twice. `modal-base` uses a
 * single instance.
 *
 * Usage:
 *   private _a11y = new DialogA11y(this, {
 *     getDialog: () => this.querySelector(".modal-dialog"),
 *     onDismiss: () => this.handleCancel(),
 *   });
 *   // then, whenever the open state changes:
 *   this._a11y.setOpen(isOpen);
 */

// ─── Module-level shared state ────────────────────────────────────────────────

/**
 * Ref count of open dialogs. The body scroll lock and the background
 * aria-hidden/inert are shared resources: releasing them on the first close
 * would unlock the page while a second dialog is still open. Only the
 * transition 0 → 1 applies them and 1 → 0 releases them.
 */
let openDialogCount = 0;

/** Body overflow value captured before the first dialog locked it. */
let previousBodyOverflow: string | null = null;

/**
 * Top-level sections hidden from assistive technology while a dialog is open.
 *
 * `main#MainContent` is deliberately absent: the dialogs are rendered inside it
 * (see `sections/main-product.liquid`), so inerting it would inert the dialog
 * itself. The focus trap plus `aria-modal="true"` covers keyboard containment;
 * portalling the overlay to <body> would be needed to inert main as well.
 */
const BACKGROUND_SELECTORS = [
  "#shopify-section-announcement-bar",
  "#shopify-section-header",
  "#shopify-section-footer",
];

function setBackgroundHidden(hide: boolean) {
  BACKGROUND_SELECTORS.forEach((sel) => {
    const el = document.querySelector<HTMLElement>(sel);
    if (!el) return;
    if (hide) {
      el.setAttribute("aria-hidden", "true");
      try {
        el.inert = true;
      } catch (_) {}
    } else {
      el.removeAttribute("aria-hidden");
      try {
        el.inert = false;
      } catch (_) {}
    }
  });
}

function acquirePageLock() {
  if (openDialogCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setBackgroundHidden(true);
  }
  openDialogCount++;
}

function releasePageLock() {
  openDialogCount = Math.max(0, openDialogCount - 1);
  if (openDialogCount === 0) {
    document.body.style.overflow = previousBodyOverflow ?? "";
    previousBodyOverflow = null;
    setBackgroundHidden(false);
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DialogA11yOptions {
  /** Returns the element carrying `role="dialog"` — the focus trap container. */
  getDialog: () => HTMLElement | null;

  /**
   * Called when the *user* dismisses the dialog (Escape). Not called when the
   * dialog is closed programmatically via `setOpen(false)`.
   */
  onDismiss: () => void;

  /**
   * Allow clicks outside the dialog to pass through to the page. Read lazily at
   * trap-activation time, since hosts may set it from an attribute or in a
   * subclass constructor, after this controller is constructed.
   */
  allowOutsideClick?: () => boolean;

  /**
   * Preferred element to return focus to on close. Falls back to whatever was
   * focused when the dialog opened.
   */
  getReturnFocus?: () => HTMLElement | null;
}

// ─── Controller ───────────────────────────────────────────────────────────────

export class DialogA11y implements ReactiveController {
  private _host: ReactiveControllerHost;
  private _options: DialogA11yOptions;

  private _isOpen = false;
  private _focusTrap: FocusTrap | null = null;
  private _previouslyFocused: HTMLElement | null = null;

  /**
   * True while we are tearing the trap down ourselves. focus-trap fires
   * `onDeactivate` for *every* deactivation, including programmatic ones, so
   * without this guard a normal close would also report a user dismissal and
   * trigger a spurious cancel.
   */
  private _deactivating = false;

  constructor(host: ReactiveControllerHost, options: DialogA11yOptions) {
    this._host = host;
    this._options = options;
    host.addController(this);
  }

  hostDisconnected() {
    // Leaving the DOM while open must still release the shared page lock.
    if (this._isOpen) {
      this._isOpen = false;
      this._teardownTrap();
      releasePageLock();
    }
  }

  /** Whether the dialog is currently open, as far as this controller knows. */
  get isOpen() {
    return this._isOpen;
  }

  /**
   * Drive the controller from the host's open state. Safe to call on every
   * render — transitions are deduplicated internally.
   */
  setOpen(open: boolean) {
    if (open === this._isOpen) return;
    this._isOpen = open;

    if (open) {
      this._onOpen();
    } else {
      this._onClose();
    }
  }

  // ─── Open / close ───────────────────────────────────────────────────────────

  private _onOpen() {
    this._previouslyFocused = document.activeElement as HTMLElement | null;
    acquirePageLock();

    // The dialog is rendered by the host, so wait for that render to land
    // before looking for the trap container.
    this._host.updateComplete.then(() => {
      // Guard against a close that raced the render.
      if (!this._isOpen) return;
      this._activateTrap();
    });
  }

  private _onClose() {
    this._teardownTrap();
    releasePageLock();
    this._restoreFocus();
  }

  private _restoreFocus() {
    const target =
      this._options.getReturnFocus?.() ?? this._previouslyFocused ?? null;
    this._previouslyFocused = null;

    if (!target || typeof target.focus !== "function") return;

    // In a modal chain (confirmation → customization) one dialog closes and the
    // next opens in the same tick. Restoring focus to the trigger in between
    // makes focus visibly bounce out of the flow, so defer the restore and skip
    // it if another dialog has taken over by then.
    requestAnimationFrame(() => {
      if (openDialogCount > 0) return;
      if (!target.isConnected) return;
      target.focus();
    });
  }

  // ─── Focus trap ─────────────────────────────────────────────────────────────

  private _activateTrap() {
    const dialog = this._options.getDialog();
    if (!dialog) {
      console.warn("[dialog-a11y] No dialog element found for focus trap");
      return;
    }

    try {
      this._focusTrap = createFocusTrap(dialog, {
        initialFocus: () => {
          // Prefer the primary form field so keyboard users can start typing
          // immediately; fall back to the first interactive element.
          const firstInput = dialog.querySelector<HTMLElement>(
            "input:not([type='hidden']), textarea, select"
          );
          if (firstInput) return firstInput;

          const firstFocusable = dialog.querySelector<HTMLElement>(
            'button, [href], [tabindex]:not([tabindex="-1"])'
          );
          return firstFocusable || dialog;
        },
        escapeDeactivates: true,
        allowOutsideClick: this._options.allowOutsideClick ?? true,
        // Focus restoration is handled in `_onClose` so it behaves the same for
        // every dismissal route (Escape, close button, overlay click).
        returnFocusOnDeactivate: false,
        onDeactivate: () => {
          if (this._deactivating) return;
          // Not a programmatic teardown, so the user pressed Escape.
          this._options.onDismiss();
        },
      });

      this._focusTrap.activate();
    } catch (error) {
      console.error("[dialog-a11y] Error creating focus trap:", error);
    }
  }

  private _teardownTrap() {
    if (!this._focusTrap) return;
    this._deactivating = true;
    try {
      this._focusTrap.deactivate();
    } finally {
      this._deactivating = false;
      this._focusTrap = null;
    }
  }
}

/**
 * Whether the user has asked for reduced motion. Dialogs use this to skip their
 * close animation so dismissal and focus return are immediate.
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}
